import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { catchError } from 'rxjs';
import { AppOperations } from '../../../app.operations';
import { DatePipe } from '../../pipes/date.pipe';
import { SidebarService } from '../../services/sidebar.service';
import { NotificationsState } from '../../states/notifications/notifications.state';
import { INotification } from '../../interfaces/notifications/INotification';
import { IOutOfOfficeBackAtOfficeData } from '../../interfaces/notifications/IOutOfOfficeBackAtOfficeData';
import { IServiceTaskCompletedData, isServiceTaskCompletedData } from '../../interfaces/notifications/IServiceTaskCompletedData';
import { IServiceTaskOverdueData, isServiceTaskOverdueData } from '../../interfaces/notifications/IServiceTaskOverdueData';
import { IWorkflowOverdueData } from '../../interfaces/notifications/IWorkflowOverdueData';
import { IWorkflowCompletedData } from '../../interfaces/notifications/IWorkflowCompletedData';
import { IWorkflowStartedData } from '../../interfaces/notifications/IWorkflowStartedData';
import { ElmWorkflowType } from '../../interfaces/employee-lifecycle/elm-workflow-type.enum';
import { NotificationClass } from '../../interfaces/notifications/NotificationClass';
import { NotificationSeverity } from '../../interfaces/notifications/NotificationSeverity';
import { NotificationType } from '../../interfaces/notifications/NotificationType';
import { DismissAllNotifications, DismissNotification } from '../../states/notifications/notifications.actions';
import { NotificationService } from '../../services/notification.service';
import {
  IEmployeeOffboardingFailureData,
  isEmployeeOffboardingFailureData,
} from '../../interfaces/notifications/IEmployeeOffboardingFailureData';
import { IPassportReminderData, isPassportReminderData } from '../../interfaces/notifications/IPassportReminderData';
import { IImpersonationEndingData, isImpersonationEndingData } from '../../interfaces/notifications/IImpersonationEndingData';
import { DeepLinkDialog, DIALOG_QUERY_PARAM } from '../../enums/DeepLinkDialog.enum';
import { DateTime } from 'luxon';
import { IBudgetThresholdData, isBudgetThresholdData } from '../../interfaces/notifications/IBudgetThresholdData';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslatePipe, DatePipe, MatIconModule],
})
export class NotificationsComponent {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly sidebarService = inject(SidebarService);
  private readonly notificationService = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  protected readonly notifications = this.store.selectSignal<INotification[]>(NotificationsState.pendingNotifications);
  protected readonly hasDismissibleNotifications = computed(() => this.notifications().some(notification => this.canDismiss(notification)));
  protected readonly NotificationSeverity = NotificationSeverity;
  protected readonly NotificationType = NotificationType;

  protected titleKey(notification: INotification): string {
    return `notifications.types.${notification.type}.title`;
  }

  protected descriptionKey(notification: INotification): string {
    const offboardingFailure = this.employeeOffboardingFailureData(notification);
    if (offboardingFailure) {
      return `notifications.types.${notification.type}.description.${offboardingFailure.reason}`;
    }

    const overdueTask = this.serviceTaskOverdueData(notification);
    if (overdueTask) {
      return `notifications.types.${notification.type}.description.${overdueTask.daysOverdue === 1 ? 'one-day' : 'many-days'}`;
    }

    const passport = this.passportReminderData(notification);
    if (passport && notification.type === NotificationType.PASSPORT_EXPIRING) {
      return `notifications.types.${notification.type}.description.${passport.daysRemaining === 1 ? 'one-day' : 'many-days'}`;
    }

    const impersonation = this.impersonationEndingData(notification);
    if (impersonation) {
      const role = impersonation.canExtend ? 'owner' : 'impersonator';
      return `notifications.types.${notification.type}.description.${role}.${impersonation.daysRemaining === 1 ? 'one-day' : 'many-days'}`;
    }

    const budgetThreshold = this.budgetThresholdData(notification);
    if (budgetThreshold) {
      return `notifications.types.${notification.type}.description.${budgetThreshold.threshold >= 100 ? 'exceeded' : 'warning'}`;
    }

    const workflowData = this.lifecycleWorkflowData(notification);
    if (workflowData?.workflowType != null) {
      if (this.hasLifecycleDescriptionContext(notification, workflowData)) {
        return `notifications.types.${notification.type}.description.${this.workflowTypeKey(workflowData.workflowType)}`;
      }

      return `notifications.types.${notification.type}.description.legacy`;
    }

    return `notifications.types.${notification.type}.description`;
  }

  protected descriptionParams(
    notification: INotification,
  ):
    | IEmployeeOffboardingFailureData
    | IServiceTaskCompletedData
    | (IServiceTaskOverdueData & { taskTypeName: string })
    | IWorkflowCompletedData
    | IWorkflowOverdueData
    | (IWorkflowStartedData & { serviceLaneNames?: string })
    | (IPassportReminderData & { date: string })
    | (IImpersonationEndingData & { date: string })
    | (Omit<IBudgetThresholdData, 'budget' | 'totalCosts'> & { budget: string; totalCosts: string })
    | undefined {
    const offboardingFailure = this.employeeOffboardingFailureData(notification);
    if (offboardingFailure) {
      return offboardingFailure;
    }

    const passport = this.passportReminderData(notification);
    if (passport) {
      return { ...passport, date: this.formatDate(passport.expirationDate) };
    }

    const impersonation = this.impersonationEndingData(notification);
    if (impersonation) {
      return { ...impersonation, date: this.formatDate(impersonation.validTo) };
    }

    const budgetThreshold = this.budgetThresholdData(notification);
    if (budgetThreshold) {
      return {
        ...budgetThreshold,
        budget: this.formatAmount(budgetThreshold.budget),
        totalCosts: this.formatAmount(budgetThreshold.totalCosts),
      };
    }

    const completedTask = this.serviceTaskCompletedData(notification);
    if (completedTask) {
      return {
        ...completedTask,
        serviceLaneName: this.serviceLaneLabel(completedTask.serviceLaneName),
      };
    }

    const overdueTask = this.serviceTaskOverdueData(notification);
    if (overdueTask) {
      return {
        ...overdueTask,
        serviceLaneName: this.serviceLaneLabel(overdueTask.serviceLaneKey),
        taskTypeName: this.taskTypeLabel(overdueTask.taskTypeKey),
      };
    }

    const startedWorkflow = this.workflowStartedData(notification);
    if (startedWorkflow) {
      return {
        ...startedWorkflow,
        serviceLaneNames: [...new Set(startedWorkflow.tasks.map(task => this.serviceLaneLabel(task.serviceLaneName)))]
          .filter(Boolean)
          .join(', '),
      };
    }

    return this.lifecycleWorkflowData(notification);
  }

  protected isClickable(notification: INotification): boolean {
    return (
      this.outOfOfficeRequestId(notification) != null ||
      this.employeeOffboardingFailureData(notification) != null ||
      this.serviceTaskCompletedData(notification) != null ||
      this.serviceTaskOverdueData(notification) != null ||
      this.passportReminderData(notification) != null ||
      this.impersonationEndingData(notification)?.canExtend === true ||
      this.budgetThresholdData(notification) != null ||
      this.lifecycleWorkflowData(notification) != null
    );
  }

  protected canDismiss(notification: INotification): boolean {
    return notification.class === NotificationClass.NORMAL;
  }

  protected onNotificationClick(notification: INotification): void {
    if (this.passportReminderData(notification)) {
      this.sidebarService.close();
      void this.router.navigate(['personal-information']);
      return;
    }

    if (this.impersonationEndingData(notification)?.canExtend) {
      this.sidebarService.close();
      // The header opens the dialog for this parameter, the same way as for the mail link.
      void this.router.navigate([], {
        queryParams: { [DIALOG_QUERY_PARAM]: DeepLinkDialog.IMPERSONATION },
        queryParamsHandling: 'merge',
      });
      return;
    }

    const budgetThreshold = this.budgetThresholdData(notification);
    if (budgetThreshold) {
      this.sidebarService.close();
      const area = notification.type === NotificationType.ACCOUNT_BUDGET_THRESHOLD ? 'accounts' : 'projects';
      void this.router.navigate([area, 'details', budgetThreshold.id]);
      return;
    }

    const offboardingFailure = this.employeeOffboardingFailureData(notification);
    if (offboardingFailure) {
      this.sidebarService.close();
      void this.router.navigate(['employees', 'details', offboardingFailure.employeeId]);
      return;
    }

    const overdueTask = this.serviceTaskOverdueData(notification);
    if (overdueTask) {
      this.sidebarService.close();
      void this.router.navigate(['service-line-tasks'], {
        queryParams: { taskId: overdueTask.serviceTaskId },
      });
      return;
    }

    const serviceTaskCreated = this.workflowStartedData(notification);
    if (notification.type === NotificationType.SERVICE_TASK_CREATED && serviceTaskCreated) {
      this.sidebarService.close();
      void this.router.navigate(['service-line-tasks'], {
        queryParams: {
          employeeId: serviceTaskCreated.employeeId,
          serviceLaneId: [...new Set(serviceTaskCreated.tasks.map(task => task.serviceLaneId))],
        },
      });
      return;
    }

    if (notification.type !== NotificationType.OUT_OF_OFFICE_BACK_AT_OFFICE) {
      const workflowId = this.serviceTaskCompletedData(notification)?.workflowId ?? this.lifecycleWorkflowData(notification)?.workflowId;
      if (workflowId == null) {
        return;
      }

      this.sidebarService.close();
      void this.router.navigate(['employees', 'lifecycle', workflowId]);
      return;
    }

    const requestId = this.outOfOfficeRequestId(notification);
    if (requestId == null) {
      return;
    }

    this.sidebarService.close();
    void this.router.navigate(['out-of-office', 'details', requestId]);
  }

  protected dismissNotification(notification: INotification, event: MouseEvent): void {
    event.stopPropagation();
    this.store
      .dispatch(new DismissNotification(notification.id))
      .pipe(
        catchError(() => AppOperations.showErrorMessage(this.notificationService, 'notifications.error.dismissNotification')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected dismissAllNotifications(): void {
    this.store
      .dispatch(new DismissAllNotifications())
      .pipe(
        catchError(() => AppOperations.showErrorMessage(this.notificationService, 'notifications.error.dismissAllNotifications')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private outOfOfficeRequestId(notification: INotification): number | undefined {
    if (notification.type !== NotificationType.OUT_OF_OFFICE_BACK_AT_OFFICE) {
      return undefined;
    }

    return (notification.data as IOutOfOfficeBackAtOfficeData | null)?.outOfOfficeRequestId;
  }

  private employeeOffboardingFailureData(notification: INotification): IEmployeeOffboardingFailureData | undefined {
    if (notification.type !== NotificationType.EMPLOYEE_OFFBOARDING_FAILED) {
      return undefined;
    }

    return isEmployeeOffboardingFailureData(notification.data) ? notification.data : undefined;
  }

  private serviceTaskCompletedData(notification: INotification): IServiceTaskCompletedData | undefined {
    if (notification.type !== NotificationType.SERVICE_TASK_COMPLETED) {
      return undefined;
    }

    return isServiceTaskCompletedData(notification.data) ? notification.data : undefined;
  }

  private serviceTaskOverdueData(notification: INotification): IServiceTaskOverdueData | undefined {
    if (notification.type !== NotificationType.SERVICE_TASK_OVERDUE) {
      return undefined;
    }

    return isServiceTaskOverdueData(notification.data) ? notification.data : undefined;
  }

  private passportReminderData(notification: INotification): IPassportReminderData | undefined {
    if (notification.type !== NotificationType.PASSPORT_EXPIRING && notification.type !== NotificationType.PASSPORT_EXPIRED) {
      return undefined;
    }

    return isPassportReminderData(notification.data) ? notification.data : undefined;
  }

  private impersonationEndingData(notification: INotification): IImpersonationEndingData | undefined {
    if (notification.type !== NotificationType.IMPERSONATION_ENDING) {
      return undefined;
    }

    return isImpersonationEndingData(notification.data) ? notification.data : undefined;
  }

  // Payload dates are zone-less calendar dates; parsing them as local time keeps the day stable.
  private formatDate(isoDate: string): string {
    return DateTime.fromISO(isoDate).toFormat('dd.MM.yyyy');
  }

  private budgetThresholdData(notification: INotification): IBudgetThresholdData | undefined {
    if (
      notification.type !== NotificationType.ACCOUNT_BUDGET_THRESHOLD &&
      notification.type !== NotificationType.PROJECT_BUDGET_THRESHOLD
    ) {
      return undefined;
    }

    return isBudgetThresholdData(notification.data) ? notification.data : undefined;
  }

  private formatAmount(value: number): string {
    const locale = this.translate.getCurrentLang() === 'de' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);
  }

  private lifecycleWorkflowData(
    notification: INotification,
  ): IWorkflowOverdueData | IWorkflowCompletedData | (IWorkflowStartedData & { serviceLaneNames?: string }) | undefined {
    if (
      notification.type !== NotificationType.WORKFLOW_OVERDUE &&
      notification.type !== NotificationType.WORKFLOW_COMPLETED &&
      notification.type !== NotificationType.WORKFLOW_STARTED &&
      notification.type !== NotificationType.SERVICE_TASK_CREATED
    ) {
      return undefined;
    }

    return (notification.data as IWorkflowOverdueData | IWorkflowCompletedData | IWorkflowStartedData | null) ?? undefined;
  }

  private hasLifecycleDescriptionContext(
    notification: INotification,
    workflowData: IWorkflowOverdueData | IWorkflowCompletedData | IWorkflowStartedData,
  ): boolean {
    if (!workflowData.employeeName) {
      return false;
    }

    if (notification.type !== NotificationType.SERVICE_TASK_CREATED) {
      return true;
    }

    return (workflowData as IWorkflowStartedData).tasks.some(task => Boolean(task.serviceLaneName));
  }

  private workflowStartedData(notification: INotification): (IWorkflowStartedData & { serviceLaneNames?: string }) | undefined {
    if (notification.type !== NotificationType.WORKFLOW_STARTED && notification.type !== NotificationType.SERVICE_TASK_CREATED) {
      return undefined;
    }

    return this.lifecycleWorkflowData(notification) as IWorkflowStartedData | undefined;
  }

  private workflowTypeKey(workflowType: ElmWorkflowType): string {
    return workflowType === ElmWorkflowType.Onboarding
      ? 'onboarding'
      : workflowType === ElmWorkflowType.Change
        ? 'employee-change'
        : 'offboarding';
  }

  private serviceLaneLabel(serviceLaneName: string | undefined): string {
    if (!serviceLaneName) {
      return '';
    }

    const key = `employees.lifecycleDetail.serviceLanes.${serviceLaneName}`;
    const translation = this.translate.instant(key);
    return translation === key ? serviceLaneName : translation;
  }

  private taskTypeLabel(taskTypeKey: string): string {
    const key = `employees.lifecycleDetail.tasks.${taskTypeKey}`;
    const translation = this.translate.instant(key);
    return translation === key ? taskTypeKey : translation;
  }
}
